import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, firestore } from '../firebase';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    disableNetwork,
    enableNetwork,
} from "firebase/firestore";

// Hook to get latest image data
export const useTryonData = () => {
    console.log('[useTryonData] HOOK CALLED - Top Level');

    const [galleryImages, setGalleryImages] = useState([]);
    const [isLoadingInitial, setIsLoadingInitial] = useState(true);
    const [currentUid, setCurrentUid] = useState(null);

    // This function sets up the Firestore listener and returns an unsubscribe function.
    const setupFirestoreListener = useCallback((uid) => {
        console.log(`Setting up Firestore listeners for user: ${uid}`);
        setIsLoadingInitial(true);

        const imageCollectionRef = collection(firestore, "users", uid, "image");
        const imageQuery = query(imageCollectionRef, orderBy("createdAt", "desc"));
        
        const upscaleCollectionRef = collection(firestore, "users", uid, "upscale");
        const upscaleQuery = query(upscaleCollectionRef, orderBy("createdAt", "desc"));

        const videoCollectionRef = collection(firestore, "users", uid, "video");
        const videoQuery = query(videoCollectionRef, orderBy("createdAt", "desc"));

        let imagesFromListener = [];
        let upscalesFromListener = [];
        let videosFromListener = [];

        const mergeData = () => {
            console.log(`Merging data: ${imagesFromListener.length} images, ${upscalesFromListener.length} upscales, ${videosFromListener.length} videos.`);

            // Get all successful upscaled images and a set of their original URLs for quick lookup
            const successfulUpscales = upscalesFromListener.filter(
                u => u.status === 'succeeded' && u.upscaledUrl && u.originalUrl
            );

            // Create a map of original URLs to their upscaled versions for quick lookup
            const upscaleMap = new Map(
                successfulUpscales.map(u => [u.originalUrl, u])
            );

            // Create the gallery list by processing original images in their original order
            const processedImages = imagesFromListener.map(image => {
                // If this image has been upscaled, use the upscaled version
                if (upscaleMap.has(image.imageUrl)) {
                    const upscale = upscaleMap.get(image.imageUrl);
                    return {
                        id: upscale.id,
                        imageUrl: upscale.upscaledUrl,
                        isLoading: false,
                        createdAt: image.createdAt, // Keep the original creation date
                        originalUrl: image.imageUrl,
                        baseImageId: image.baseImageId || image.id,
                        prompt: image.prompt || null, // Keep the prompt from original
                        mediaType: 'image',
                    };
                }
                // Otherwise keep the original image
                return { ...image, mediaType: 'image' };
            });

            // Merge images and videos, sorted by createdAt desc
            const mergedItems = [...processedImages, ...videosFromListener].sort(
                (a, b) => {
                    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
                    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
                    return tb - ta;
                }
            );

            setGalleryImages(mergedItems);
            setIsLoadingInitial(false);
            console.log("Gallery items updated, count:", mergedItems.length);
        };

        const imageUnsubscribe = onSnapshot(imageQuery, (querySnapshot) => {
            console.log(`Firestore snapshot received for 'image' collection: ${querySnapshot.size} docs`);
            const images = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                // Skip images that are marked hidden in Firestore
                if (data.hidden === true) {
                    return;
                }
                // Skip avatar generations from the /demo flow so they only show
                // in the demo gallery, not the regular tryon gallery.
                if (data.avatarImage === true) {
                    return;
                }
                const processedUrl = data.processedImageUrlR2;
                const status = (data.generationStatus || '').toLowerCase();
                const isFailed = ['failed', 'error', 'cancelled', 'canceled'].includes(status) || !!data.error;
                images.push({
                    id: doc.id,
                    baseImageId: doc.id,
                    imageUrl: processedUrl || null,
                    isLoading: !processedUrl && !isFailed,
                    isFailed,
                    error: data.error || null,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    prompt: data.prompt || null,
                });
            });
            imagesFromListener = images;
            mergeData();
        }, (error) => {
            console.error("Error in Firestore listener (image collection):", error);
            // On error, we might still want to merge with whatever we have
            imagesFromListener = [];
            mergeData();
        });

        const upscaleUnsubscribe = onSnapshot(upscaleQuery, (querySnapshot) => {
            console.log(`Firestore snapshot received for 'upscale' collection: ${querySnapshot.size} docs`);
            const upscales = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                upscales.push({
                    id: doc.id,
                    originalUrl: data.processedImageUrlR2, // The image that was sent for upscaling
                    upscaledUrl: data.upscaleUrl, // The final upscaled URL from R2
                    status: data.status,
                    createdAt: data.createdAt?.toDate() || new Date(), // Extract creation date
                });
            });
            upscalesFromListener = upscales;
            mergeData();
        }, (error) => {
            console.error("Error in Firestore listener (upscale collection):", error);
            upscalesFromListener = [];
            mergeData();
        });

        const videoUnsubscribe = onSnapshot(videoQuery, (querySnapshot) => {
            console.log(`Firestore snapshot received for 'video' collection: ${querySnapshot.size} docs`);
            const videos = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.hidden === true) return;
                const processedUrl = data.processedVideoUrlR2;
                const status = (data.generationStatus || '').toLowerCase();
                const isFailed = ['failed', 'error', 'cancelled', 'canceled'].includes(status) || !!data.error;
                videos.push({
                    id: docSnap.id,
                    baseImageId: docSnap.id,
                    mediaType: 'video',
                    videoUrl: processedUrl || null,
                    imageUrl: data.imageUrl || null, // poster fallback while loading
                    motionVideoUrl: data.motionVideoUrl || null,
                    isLoading: !processedUrl && !isFailed,
                    isFailed,
                    error: data.error || null,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    prompt: null,
                });
            });
            videosFromListener = videos;
            mergeData();
        }, (error) => {
            console.error("Error in Firestore listener (video collection):", error);
            videosFromListener = [];
            mergeData();
        });


        // Return a function that unsubscribes from all listeners
        return () => {
            console.log("Unsubscribing from Firestore listeners.");
            imageUnsubscribe();
            upscaleUnsubscribe();
            videoUnsubscribe();
        };
    }, []);

    // Effect for handling auth changes and setting up/tearing down the listener
    useEffect(() => {
        console.log('[useTryonData] useEffect for auth started.');
        let unsubscribeFirestore = null;

        // Function to handle user state (new or existing user)
        const handleUser = (user) => {
            if (unsubscribeFirestore) {
                console.log("[useTryonData] Previous Firestore listener found (in handleUser), unsubscribing.");
                unsubscribeFirestore();
                unsubscribeFirestore = null;
            }

            if (user) {
                console.log("[useTryonData] User detected in handleUser (", user.uid, "), preparing to set up new listener.");
                setCurrentUid(user.uid);
                console.log("[useTryonData] Calling setupFirestoreListener from handleUser for user:", user.uid);
                unsubscribeFirestore = setupFirestoreListener(user.uid);
            } else {
                console.log("[useTryonData] No user detected in handleUser. Clearing gallery images and UID.");
                setCurrentUid(null);
                setGalleryImages([]);
                setIsLoadingInitial(false);
            }
        };

        // Subscribe to auth state changes
        console.log("[useTryonData] Subscribing to onAuthStateChanged.");
        const onAuthStateChangedSubscription = auth.onAuthStateChanged(user => {
            console.log('[useTryonData] onAuthStateChanged event fired.');
            handleUser(user);
        });

        // Check current user synchronously on mount, in case onAuthStateChanged doesn't fire immediately for an existing session
        console.log("[useTryonData] Checking auth.currentUser synchronously on mount.");
        const currentUser = auth.currentUser;
        if (currentUser) {
            console.log("[useTryonData] User already logged in on mount (", currentUser.uid, "). Calling handleUser.");
            handleUser(currentUser);
        } else {
            console.log("[useTryonData] No user initially logged in on mount.");
            // Potentially set isLoadingInitial to false if we know there's no user and no immediate auth change expected
            // For now, handleUser(null) will be called by onAuthStateChanged if it confirms no user, or handleUser(user) if it finds one.
        }

        // Cleanup function for the useEffect
        return () => {
            console.log("[useTryonData] Cleaning up auth listener (onAuthStateChangedSubscription) for useTryonData.");
            onAuthStateChangedSubscription(); // Unsubscribe from auth state changes
            if (unsubscribeFirestore) {
                console.log("[useTryonData] Cleaning up active Firestore listener on unmount/auth change.");
                unsubscribeFirestore(); // Unsubscribe from Firestore if active
            }
        };
    }, [setupFirestoreListener]); // setupFirestoreListener is a dependency

    // Force Firestore to drop & re-establish all snapshot listeners. We use
    // this when we know the backend has updated something but the realtime
    // websocket missed the event (browser tab was throttled, network blip,
    // etc.). disableNetwork → enableNetwork triggers a clean reconnect.
    const refreshingRef = useRef(false);
    const refreshTryonData = useCallback(async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;
        try {
            console.log("[useTryonData] Forcing Firestore listener reconnect.");
            await disableNetwork(firestore);
            // Tiny delay so any in-flight close completes before we re-open.
            await new Promise((r) => setTimeout(r, 50));
            await enableNetwork(firestore);
        } catch (err) {
            console.warn("[useTryonData] refresh failed:", err);
        } finally {
            refreshingRef.current = false;
        }
    }, []);

    // Re-establish listeners when the tab becomes visible again. Browsers
    // (especially mobile Safari and Chrome on Android) throttle background
    // tabs hard enough that the Firestore long-poll connection can die.
    // Without this, a user who briefly switches tabs comes back to a
    // gallery that still says "loading" even though the doc has long since
    // succeeded server-side.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                refreshTryonData();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [refreshTryonData]);

    return { galleryImages, isLoadingInitial, refreshTryonData };
};
   